// The account's appearance choices, kept in step with this device (scenario
// M2, PLAN-2026-10-02-your-account-follows-you.md §2–§5), and the League
// page's arrangement and where each league opens (L3, §6).
//
// THE PROVIDERS STAY WHERE THEY ARE. The look, the theme and the detail level
// still live in their own providers and their own browser caches, which is
// what paints the first frame before anything has been asked (§4) and what a
// visitor on the tour has. This sits beside them for a signed-in person:
//
//   - an answer from `/api/me` (the feed) brings the account's choices, and
//     they are put on screen — except this device's own detail level and text
//     size, when it keeps its own;
//   - a change made here is sent as a `PUT /api/me/prefs` of the fields that
//     changed — except those two, when this device keeps its own, which stay
//     in this browser and are never sent (§3).
//
// HOW A CHANGE MADE HERE IS TOLD FROM ONE THIS FILE MADE. A value counts as
// the person's when it MOVED since the watcher last looked (`seen`) AND it is
// not what this file last put there (`expected`). Both halves matter, and the
// review of 2026-10-02 found what goes wrong with only the second: in the
// commit where somebody signs in, the account's answer is applied and the
// watcher runs with the screen as it was before — every value differs from
// `expected`, and the tour's choices were sent over the account's. Nothing on
// screen had moved, so with `seen` nothing is sent. And a change made before
// the first answer (nothing `expected` yet) is the person's, so it is sent,
// or kept to send, rather than dropped and then undone when the answer comes.
//
// AN ACCOUNT WITH NOTHING SAVED (every account before this) takes this
// device's choices, so what somebody chose on the tour carries over (§4).
//
// THE ACCOUNT'S CHOICES ARE CACHED per account in this browser, so where a
// league opens (the hub's cards, L3) and the League page's order are right
// from the first frame. The cache is never applied to the look, the theme or
// the level; their own caches paint those.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  UserPrefs,
  type AchievementPrefs,
  type ChatPrefs,
  type PrefsUpdateInput,
} from '@fsp/domain';
import { DEFAULT_LOOK, type DetailLevel, type Theme } from '@fsp/theme';
import { parseLook } from '../looks/registry';
import { useTheme } from '../theme/ThemeProvider';
import { tell } from '../kit/ui';
import { messageOf, send } from './api';
import { useDetailContext } from './detail';
import { useLookContext } from './look';
import { forgetMe, onMe, type MeNews } from './meFeed';
import { useSession } from './session';
import { storageKey } from './storage';

type FontScale = Theme['fontScale'];

/** This device's own detail level and text size (§3), per account on it. */
export interface DeviceOwn {
  detail: DetailLevel;
  fontScale: FontScale;
}

/** Per account, so two people sharing a computer each keep their own (§3). */
const OWN_KEY = storageKey('device-own.v1');
/** A change that could not reach the site, sent with the next save or answer (§4). */
const PENDING_KEY = storageKey('prefs.pending.v1');
/** The account's choices as last heard, per account: the first frame's copy. */
const ACCOUNT_KEY = storageKey('prefs.account.v1');

function readMap<T>(key: string): Record<string, T> {
  try {
    const raw = localStorage.getItem(key);
    const parsed: unknown = raw === null ? null : JSON.parse(raw);
    return parsed !== null && typeof parsed === 'object' ? (parsed as Record<string, T>) : {};
  } catch {
    return {};
  }
}

function writeEntry<T>(key: string, userId: string, value: T | null): void {
  try {
    const map = readMap<T>(key);
    if (value === null) delete map[userId];
    else map[userId] = value;
    localStorage.setItem(key, JSON.stringify(map));
  } catch {
    // Storage blocked: the choice holds for this page load.
  }
}

function readOwn(userId: string): DeviceOwn | null {
  const own = readMap<DeviceOwn>(OWN_KEY)[userId];
  return own !== undefined && typeof own.detail === 'string' && typeof own.fontScale === 'number'
    ? own
    : null;
}

function readCachedAccount(userId: string): UserPrefs | null {
  const parsed = UserPrefs.safeParse(readMap<unknown>(ACCOUNT_KEY)[userId]);
  return parsed.success ? parsed.data : null;
}

/** Two changes as one: the later wins field by field, and leagues merge. */
function mergeUpdates(a: PrefsUpdateInput, b: PrefsUpdateInput): PrefsUpdateInput {
  return {
    ...a,
    ...b,
    ...(a.opensOn || b.opensOn ? { opensOn: { ...a.opensOn, ...b.opensOn } } : {}),
  };
}

/** A layout change as the account will hold it, for showing it before the answer. */
function withLayout(prefs: UserPrefs, update: PrefsUpdateInput): UserPrefs {
  return {
    ...prefs,
    ...(update.leaguePage !== undefined ? { leaguePage: update.leaguePage } : {}),
    ...(update.opensOn !== undefined
      ? {
          opensOn: Object.fromEntries(
            Object.entries({ ...prefs.opensOn, ...update.opensOn }).filter(([, w]) => w !== 'team'),
          ),
        }
      : {}),
  };
}

/** A theme as a comparable string, with and without its text size. */
const keyOf = (theme: Theme): string => JSON.stringify(theme);
const restOf = (theme: Theme): string => keyOf({ ...theme, fontScale: 1 });

/** What is on screen, in the terms a change is noticed in. */
interface Shown {
  look: string;
  rest: string;
  fontScale: FontScale;
  detail: DetailLevel;
}

const shownOf = (look: string, theme: Theme, detail: DetailLevel): Shown => ({
  look,
  rest: restOf(theme),
  fontScale: theme.fontScale,
  detail,
});

type Saved = { status: 'ok'; prefs: UserPrefs } | { status: 'pending' } | { status: 'refused' };

interface AccountPrefsValue {
  /** The account's choices, as last heard; null signed out or never heard on this device. */
  account: UserPrefs | null;
  /** This device keeps its own detail level and text size (§3). */
  deviceOwn: boolean;
  setDeviceOwn: (on: boolean) => void;
  /**
   * Save where a league opens or the League page's arrangement (L3), shown at
   * once. False only when the site refused it, and then it is put back.
   */
  saveLayout: (update: Pick<PrefsUpdateInput, 'leaguePage' | 'opensOn'>) => Promise<boolean>;
  /**
   * *Tell my leagues when I find one* (A4, ACHIEVEMENTS.md §12), shown at
   * once. False only when the site refused it, and then it is put back.
   */
  saveAchievements: (next: AchievementPrefs) => Promise<boolean>;
  /**
   * Whether the bots' lines show in my leagues' chats (B-299), shown at once.
   * False only when the site refused it, and then it is put back.
   */
  saveChat: (next: ChatPrefs) => Promise<boolean>;
}

const AccountPrefsContext = createContext<AccountPrefsValue | null>(null);

export function AccountPrefsProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const userId = session?.userId ?? null;
  const { look, setLook } = useLookContext();
  const { theme, setTheme } = useTheme();
  const { detail, setDetail } = useDetailContext();

  const [account, setAccountState] = useState<{ userId: string; prefs: UserPrefs } | null>(null);
  const [own, setOwn] = useState<{ userId: string; own: DeviceOwn | null } | null>(null);
  const ownNow = own !== null && own.userId === userId ? own.own : null;
  const accountNow = account !== null && account.userId === userId ? account.prefs : null;

  /** What this file last put on screen, for the account it was for. */
  const expected = useRef<(Shown & { userId: string }) | null>(null);
  /** What the watcher saw last time it looked. */
  const seen = useRef<Shown | null>(null);
  /** When the last change here was made or saved: an answer asked before it is stale. */
  const changedAt = useRef(0);
  const saving = useRef(0);

  // What is on screen, for the feed's listener, which outlives renders.
  const live = useRef({ look, theme, detail, userId, setLook, setTheme, setDetail });
  live.current = { look, theme, detail, userId, setLook, setTheme, setDetail };

  const setAccount = useCallback((uid: string, prefs: UserPrefs) => {
    writeEntry(ACCOUNT_KEY, uid, prefs);
    setAccountState({ userId: uid, prefs });
  }, []);

  /** Put the account's choices on screen, this device's own kept. */
  const apply = useCallback((uid: string, prefs: UserPrefs) => {
    const now = live.current;
    const mine = readOwn(uid);
    // A look this build does not have is drawn as its default and not written
    // back: the account keeps the other client's choice (§5).
    const nextLook = parseLook(prefs.look) === prefs.look ? prefs.look : DEFAULT_LOOK;
    const nextTheme: Theme = mine ? { ...prefs.theme, fontScale: mine.fontScale } : prefs.theme;
    const nextDetail = mine ? mine.detail : prefs.detail;
    expected.current = { userId: uid, ...shownOf(nextLook, nextTheme, nextDetail) };
    if (nextLook !== now.look) now.setLook(nextLook);
    if (keyOf(nextTheme) !== keyOf(now.theme)) now.setTheme(nextTheme);
    if (nextDetail !== now.detail) now.setDetail(nextDetail);
  }, []);

  /**
   * Send a change, with anything still waiting from before it — a save that
   * clears the queue has to have carried it. `quiet` for the ones the person
   * did not make here.
   */
  const save = useCallback(
    async (uid: string, update: PrefsUpdateInput, quiet = false): Promise<Saved> => {
      const waiting = readMap<PrefsUpdateInput>(PENDING_KEY)[uid];
      const body = waiting === undefined ? update : mergeUpdates(waiting, update);
      saving.current += 1;
      const reply = await send('PUT', '/me/prefs', body, UserPrefs);
      saving.current -= 1;
      changedAt.current = Date.now();
      if (reply.status === 'ok') {
        writeEntry(PENDING_KEY, uid, null);
        if (live.current.userId === uid) setAccount(uid, reply.data);
        return { status: 'ok', prefs: reply.data };
      }
      if (reply.status === 'unreachable') {
        // Kept, and sent with the next save or the next answer (§4). A write
        // nobody answered may have landed, but sending the same values again
        // is harmless, and dropping them is not: a timeout keeps them too.
        writeEntry(PENDING_KEY, uid, body);
        if (!quiet) {
          tell(
            reply.failure.code === 'unreachable'
              ? 'Changed here. Could not reach the site, so your other devices will not see it yet.'
              : `Changed here. ${reply.failure.message}`,
          );
        }
        return { status: 'pending' };
      }
      // Refused: a queue the site will not take would block every save after it.
      writeEntry(PENDING_KEY, uid, null);
      if (!quiet) {
        tell(messageOf(reply, 'That did not save to your account.', 'That did not save.'), 'bad');
      }
      return { status: 'refused' };
    },
    [setAccount],
  );

  // The account's choices, from every answer `/api/me` and the bell's pulse give.
  //
  // Subscribed afresh for each account: a sign-in publishes its answer before
  // this component has re-rendered as the new account, so the listener would
  // have ignored it — and the feed replays the latest answer to a new
  // subscriber, which is how that one arrives.
  useEffect(() => {
    expected.current = null;
    setAccountState(
      userId === null
        ? null
        : ((p) => (p === null ? null : { userId, prefs: p }))(readCachedAccount(userId)),
    );
    setOwn(userId === null ? null : { userId, own: readOwn(userId) });
    if (userId === null) {
      forgetMe();
      return;
    }
    return onMe((news: MeNews, askedAt: number) => {
      const uid = live.current.userId;
      if (uid === null || news.userId !== uid || news.prefs === undefined) return;
      // Asked before a change made here, or while one is on its way: it
      // cannot know about that change and must not undo it.
      if (askedAt < changedAt.current || saving.current > 0) return;
      if (readMap<PrefsUpdateInput>(PENDING_KEY)[uid] !== undefined) {
        // A change that could not go: it goes now, and what the account holds
        // after it is what is put on screen.
        void save(uid, {}, true).then((r) => {
          if (r.status === 'ok' && live.current.userId === uid) apply(uid, r.prefs);
        });
        return;
      }
      if (news.prefs === null) {
        // Nothing saved yet: this device's choices become the account's.
        const now = live.current;
        expected.current = { userId: uid, ...shownOf(now.look, now.theme, now.detail) };
        void save(uid, { look: now.look, theme: now.theme, detail: now.detail }, true);
        return;
      }
      setAccount(uid, news.prefs);
      apply(uid, news.prefs);
    });
  }, [userId, apply, save, setAccount]);

  // A change made here: sent, or kept on this device when it is its own.
  useEffect(() => {
    const now = shownOf(look, theme, detail);
    const prev = seen.current;
    seen.current = now;
    if (userId === null || prev === null) return;
    const exp =
      expected.current !== null && expected.current.userId === userId ? expected.current : null;
    const moved = (k: keyof Shown) => now[k] !== prev[k] && (exp === null || now[k] !== exp[k]);
    const update: PrefsUpdateInput = {};
    if (moved('look')) update.look = look;
    if (readOwn(userId) !== null) {
      if (moved('detail') || moved('fontScale')) {
        const next = { detail, fontScale: theme.fontScale };
        writeEntry(OWN_KEY, userId, next);
        setOwn({ userId, own: next });
      }
      // The rest of the theme is still the account's, with the account's size.
      if (moved('rest')) {
        update.theme = { ...theme, fontScale: accountNow?.theme.fontScale ?? theme.fontScale };
      }
    } else {
      if (moved('rest') || moved('fontScale')) update.theme = theme;
      if (moved('detail')) update.detail = detail;
    }
    // Only what the person moved becomes expected. Writing the whole screen
    // here undid a sign-in: in that commit `apply` has just set the account's
    // values as expected while the screen still shows the old ones, and
    // taking the old ones as expected made the account's own values, landing
    // a render later, look like a change to send back (review, 2026-10-02).
    if (exp !== null) {
      const next = { ...exp };
      for (const k of ['look', 'rest', 'fontScale', 'detail'] as const) {
        if (moved(k)) Object.assign(next, { [k]: now[k] });
      }
      expected.current = next;
    }
    if (Object.keys(update).length > 0) {
      changedAt.current = Date.now();
      void save(userId, update);
    }
    // `accountNow` is read, not watched: only the person's own changes to
    // what is on screen send anything.
  }, [look, theme, detail, userId, save]);

  const setDeviceOwn = useCallback(
    (on: boolean) => {
      if (userId === null) return;
      if (on) {
        const mine = { detail, fontScale: theme.fontScale };
        writeEntry(OWN_KEY, userId, mine);
        setOwn({ userId, own: mine });
        return;
      }
      writeEntry(OWN_KEY, userId, null);
      setOwn({ userId, own: null });
      // Back on the account's two, which is a change this file makes and so
      // is expected rather than sent.
      if (accountNow !== null) apply(userId, accountNow);
    },
    [userId, detail, theme.fontScale, accountNow, apply],
  );

  const saveLayout = useCallback(
    async (update: Pick<PrefsUpdateInput, 'leaguePage' | 'opensOn'>) => {
      if (userId === null) return false;
      const before = accountNow;
      if (before !== null) setAccount(userId, withLayout(before, update));
      changedAt.current = Date.now();
      const saved = await save(userId, update);
      if (saved.status !== 'refused') return true;
      if (before !== null) setAccount(userId, before);
      return false;
    },
    [userId, accountNow, save, setAccount],
  );

  const saveAchievements = useCallback(
    async (next: AchievementPrefs) => {
      if (userId === null) return false;
      const before = accountNow;
      if (before !== null) setAccount(userId, { ...before, achievements: next });
      changedAt.current = Date.now();
      const saved = await save(userId, { achievements: next });
      if (saved.status !== 'refused') return true;
      if (before !== null) setAccount(userId, before);
      return false;
    },
    [userId, accountNow, save, setAccount],
  );

  const saveChat = useCallback(
    async (next: ChatPrefs) => {
      if (userId === null) return false;
      const before = accountNow;
      if (before !== null) setAccount(userId, { ...before, chat: next });
      changedAt.current = Date.now();
      const saved = await save(userId, { chat: next });
      if (saved.status !== 'refused') return true;
      if (before !== null) setAccount(userId, before);
      return false;
    },
    [userId, accountNow, save, setAccount],
  );

  const value = useMemo<AccountPrefsValue>(
    () => ({
      account: accountNow,
      deviceOwn: ownNow !== null,
      setDeviceOwn,
      saveLayout,
      saveAchievements,
      saveChat,
    }),
    [accountNow, ownNow, setDeviceOwn, saveLayout, saveAchievements, saveChat],
  );
  return <AccountPrefsContext.Provider value={value}>{children}</AccountPrefsContext.Provider>;
}

export function useAccountPrefs(): AccountPrefsValue {
  const ctx = useContext(AccountPrefsContext);
  if (!ctx) throw new Error('useAccountPrefs must be used inside AccountPrefsProvider');
  return ctx;
}
